import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { AppLink } from '@/components/AppLink';
import { Callout } from '@/components/Callout';
import { Container } from '@/components/Container';
import { DraftMarker } from '@/components/DraftMarker';
import { PageHeader } from '@/components/PageHeader';
import { DIRECTORY_LIVE, SHOW_DRAFT_MARKERS } from '@/config';
import { MOCK_CENTRES, MOCK_INSTITUTES } from '@/data/centres.mock';
import { SITE } from '@/data/site';

const TABS = [
  { to: '/centres', label: 'Affiliated institutes' },
  { to: '/centres/practical', label: 'Exam centres' },
];

const INPUT = 'min-h-[50px] rounded-control border border-input-border bg-white px-3.5 text-[1rem] text-ink';
const ALL = 'All states';

interface Row {
  key: string;
  title: string; // first line of a phone card
  sub: string; // second line of a phone card
  cells: string[]; // desktop table cells
  state: string;
  haystack: string; // lower-cased text the search looks in
}

// Both tabs share this page. The search lives in the page address
// (?q=goa&state=Goa), so a search can be shared and the Back button works.
// Until the real directory is connected, the live site shows a message instead
// of the search; developing shows sample rows so the layout can be tested.
export function Centres({ practical = false }: { practical?: boolean }) {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const [state, setState] = useState(params.get('state') ?? ALL);
  useEffect(() => {
    setQ(params.get('q') ?? '');
    setState(params.get('state') ?? ALL);
  }, [params]);

  const searchOn = DIRECTORY_LIVE || SHOW_DRAFT_MARKERS;
  const noun = practical ? 'exam centres' : 'institutes';
  const singular = practical ? 'exam centre' : 'institute';
  const cols = practical ? ['Centre', 'Town', 'District', 'State'] : ['Code', 'Institute', 'Town', 'State'];

  // The whole dataset as rows; the state list and the chips are derived from it.
  const all: Row[] = useMemo(
    () =>
      practical
        ? MOCK_CENTRES.map((c) => ({
            key: c.name,
            title: c.name,
            sub: `${c.town}, ${c.district}, ${c.state}`,
            cells: [c.name, c.town, c.district, c.state],
            state: c.state,
            haystack: `${c.name} ${c.town} ${c.district}`.toLowerCase(),
          }))
        : MOCK_INSTITUTES.map((i) => ({
            key: i.code,
            title: i.name,
            sub: `${i.code} · ${i.town}, ${i.state}`,
            cells: [i.code, i.name, i.town, i.state],
            state: i.state,
            haystack: `${i.code} ${i.name} ${i.town} ${i.district}`.toLowerCase(),
          })),
    [practical],
  );
  const states = useMemo(() => [...new Set(all.map((r) => r.state))].sort(), [all]);

  const searched = params.has('q') || params.has('state');
  const rawQuery = (params.get('q') ?? '').trim();
  const stateFilter = params.get('state') ?? ALL;
  const rows = searched
    ? all.filter((r) => (stateFilter === ALL || r.state === stateFilter) && (!rawQuery || r.haystack.includes(rawQuery.toLowerCase())))
    : [];

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    next.set('q', q.trim());
    if (state !== ALL) next.set('state', state);
    setParams(next);
  };

  const emptyText = searched
    ? rawQuery
      ? `No ${noun} match “${rawQuery}”. Check the spelling or try ${practical ? 'a different district or town' : 'a town name'}.`
      : `No ${noun} found in ${stateFilter}.`
    : practical
      ? 'Choose a state to see districts and towns with exam centres.'
      : 'Enter an institute code, name or town to find an institute.';
  const countText = `${rows.length} ${rows.length === 1 ? singular : noun} found${stateFilter !== ALL ? ` in ${stateFilter}` : ''}`;

  return (
    <>
      <PageHeader
        title="Institutes & centres"
        intro="Find the institutes affiliated with ITES and the centres where ITES examinations are held."
      />
      <Container className="pt-[clamp(40px,6vw,72px)] pb-[clamp(56px,7vw,96px)]">
        <div className="flex flex-wrap gap-x-7 border-b border-rule-strong">
          {TABS.map((t) => {
            const on = (t.to === '/centres/practical') === practical;
            return (
              <Link
                key={t.to}
                to={t.to}
                aria-current={on ? 'page' : undefined}
                className={clsx(
                  '-mb-px box-content flex min-h-12 items-center border-b-[3px] text-[1.0625rem] font-semibold no-underline',
                  on ? 'border-teal-700 text-navy-900' : 'border-transparent text-muted',
                )}
              >
                {t.label}
              </Link>
            );
          })}
        </div>

        {!searchOn && (
          <Callout className="mt-6" aria-labelledby="h-directory">
            <h2 id="h-directory" className="m-0 font-serif text-h4 font-semibold text-navy-900">
              The online directory is being prepared
            </h2>
            <p className="mt-2.5 mb-3.5 max-w-[40em] text-[1.0625rem] leading-[1.55] text-ink-2">
              To find an affiliated institute or an examination centre, please contact the ITES office.
            </p>
            <p className="m-0 flex flex-wrap gap-x-6 gap-y-2 font-semibold">
              <a href={SITE.phoneHref} className="tap underline">{SITE.phone}</a>
              <a href={SITE.emailHref} className="tap underline">{SITE.email}</a>
              <AppLink href="/contact" className="tap underline">Contact &amp; directions →</AppLink>
            </p>
          </Callout>
        )}

        {searchOn && (
          <>
            <DraftMarker className="mt-4">
              DRAFT · sample rows only. The live site shows a “being prepared” message until the ITES database list is connected and the client approves publishing it
            </DraftMarker>

            <form role="search" onSubmit={onSubmit} className="mt-6 flex max-w-[880px] flex-wrap gap-2.5">
              <label htmlFor="centre-q" className="sr-only">
                {practical ? 'Search exam centres' : 'Search affiliated institutes'}
              </label>
              <input
                id="centre-q"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={practical ? 'District or town' : 'Institute code (e.g. MAH/16), name or town'}
                className={clsx(INPUT, 'min-w-0 flex-[1_1_280px]')}
              />
              <label htmlFor="centre-state" className="sr-only">State</label>
              <select
                id="centre-state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className={clsx(INPUT, 'flex-[0_1_200px] px-3')}
              >
                <option>{ALL}</option>
                {states.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
              <button
                type="submit"
                className="min-h-[50px] cursor-pointer rounded-control border-none bg-navy-800 px-[22px] font-semibold text-white"
              >
                Search
              </button>
            </form>

            <p aria-live="polite" className="mt-5 mb-0 min-h-[1.5em] text-[0.9375rem] text-muted">
              {searched && rows.length > 0 ? countText : ''}
            </p>

            <div className="mt-2 border border-rule-strong bg-white">
              {rows.length === 0 ? (
                <p className="m-0 px-4 py-10 text-center text-[1.0625rem] leading-[1.5] text-muted">{emptyText}</p>
              ) : (
                <>
                  <table className="hidden w-full border-collapse text-left sm:table">
                    <caption className="sr-only">{practical ? 'Exam centres' : 'Affiliated institutes'}</caption>
                    <thead>
                      <tr>
                        {cols.map((c) => (
                          <th
                            key={c}
                            scope="col"
                            className="bg-table-head px-4 py-3 font-mono text-[0.75rem] font-medium tracking-[0.06em] text-muted uppercase"
                          >
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.key}>
                          {r.cells.map((cell, i) => (
                            <td
                              key={i}
                              className={clsx('border-t border-rule px-4 py-3 text-[1rem]', !practical && i === 0 && 'font-mono text-[0.8125rem]')}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <ul className="m-0 list-none p-0 sm:hidden">
                    {rows.map((r) => (
                      <li key={r.key} className="border-b border-rule px-4 py-3.5 last:border-b-0">
                        <p className="m-0 font-serif text-[1.125rem] leading-[1.3] font-semibold text-navy-900">{r.title}</p>
                        <p className="mt-1 mb-0 font-mono text-[0.8125rem] text-muted">{r.sub}</p>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            {practical && states.length > 0 && (
              <>
                <h2 className="mt-10 mb-3.5 font-serif text-[1.5rem] font-semibold text-navy-900">States with exam centres</h2>
                <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                  {states.map((s) => (
                    <li key={s}>
                      <Link
                        to={`/centres/practical?q=&state=${encodeURIComponent(s)}`}
                        className="block border border-rule-strong bg-white px-3.5 py-2 max-sm:flex max-sm:min-h-11 max-sm:items-center text-[0.9375rem] font-semibold text-ink no-underline hover:border-teal-700 hover:text-teal-700"
                      >
                        {s}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </Container>
    </>
  );
}
